/**
 * @file CustomerDetailPage.tsx
 * @description Карточка клиента (phase08.5; phase19.6; phase19.31 — SC63,
 * SC67, SC68).
 *
 * Шапка — в верхней панели (`CabinetPageHeader`): «назад», имя, вид клиента;
 * под именем — телефон, почта и «клиент с …». Действия — видимыми кнопками
 * без «⋯» (`CustomerHeaderActions`): главное «+ Заказ» в панели; «Написать»
 * и «Редактировать» — в панели от 1280, уже — строкой под шапкой; «+ Задача»
 * и «Анонимизировать» — всегда строкой под шапкой (имя важнее кнопок, QA-5 D3).
 *
 * Под шапкой — плитки показателей (`CustomerMetrics`), вкладки «Обзор /
 * Заказы N / Задачи N / Лента / Профиль» (общий `Tabs`, вкладка в адресе
 * `?tab=`). «Обзор» — рабочая сводка менеджера (`CustomerOverviewTab`), лента
 * событий — своей вкладкой (блок прежний: его заменит общий компонент ленты).
 * «+ Задача» и «+ Заметка» — общая форма событий с подставленным клиентом.
 */

import { useState } from "react";
import { useParams } from "react-router";

import { CustomerActivityFeedSection } from "./sections/CustomerActivityFeedSection";
import { CustomerOrdersSection } from "./sections/CustomerOrdersSection";
import { CustomerProfileTab } from "./sections/CustomerProfileTab";
import { CustomerTasksTab } from "./sections/CustomerTasksTab";
import { useHasPermission } from "../../../auth/hooks";
import { ActivityComposerDialog } from "../../../components/crm/deal-card/ActivityComposerDialog";
import { useTabParam } from "../../../components/crm/useTabParam";
import { customerChatHref } from "../../../components/customers/card/customer-chat";
import { customerSubtitle } from "../../../components/customers/card/customer-info";
import { CustomerHeaderActions, type CustomerHeaderPart } from "../../../components/customers/card/CustomerHeaderActions";
import { CustomerMetrics } from "../../../components/customers/card/CustomerMetrics";
import { useCustomerCard } from "../../../components/customers/card/useCustomerCard";
import { useCustomerOverview } from "../../../components/customers/card/useCustomerOverview";
import { CustomerEditDialog } from "../../../components/customers/CustomerEditDialog";
import { CustomerOverviewTab } from "../../../components/customers/overview/CustomerOverviewTab";
import type { OverviewLinks } from "../../../components/customers/overview/overview-view";
import { Badge } from "../../../components/ui/badge";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { ListError, ListSkeleton } from "../../../components/ui/ListStates";
import { Tabs } from "../../../components/ui/Tabs";
import { CUSTOMER_TYPE_LABEL, customerTitle } from "../../../lib/crm-customer-view";

const CUSTOMER_TABS = ["overview", "orders", "tasks", "feed", "profile"] as const;
type CustomerTab = (typeof CUSTOMER_TABS)[number];

/** Что можно добавить из карточки клиента формой событий. */
type Composing = "TASK" | "NOTE" | null;

export function CustomerDetailPage() {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const base = `/shop/${slug}`;
  const canManage = useHasPermission("SHOP_MANAGE_CUSTOMERS");
  const canUseCrm = useHasPermission("SHOP_USE_CRM");
  const canManageOrders = useHasPermission("SHOP_MANAGE_ORDERS");
  // Право «Переписка с клиентами» (roles.md R4; на сервере — hasCustomerChat).
  const canChat = useHasPermission("SHOP_USE_CUSTOMER_CHAT");
  const card = useCustomerCard(id);
  const overview = useCustomerOverview(id);
  const [tab, setTab] = useTabParam<CustomerTab>(CUSTOMER_TABS);
  const [editing, setEditing] = useState(false);
  const [composing, setComposing] = useState<Composing>(null);
  const [tasksVersion, setTasksVersion] = useState(0);
  const { customer, stats } = card;
  const back = { to: `${base}/customers`, label: "К реестру клиентов" };

  if (!customer) {
    return (
      <div className="flex flex-col gap-4">
        <CabinetPageHeader title="Клиент" back={back} />
        {card.loading ? (
          <ListSkeleton rows={4} compact />
        ) : (
          <ListError message={card.error ?? "Клиент не найден"} onRetry={card.refresh} />
        )}
      </div>
    );
  }

  const editable = canManage && !customer.anonymizedAt;
  const active = !customer.anonymizedAt;
  const links: OverviewLinks = {
    order: (orderId) => `${base}/orders/${orderId}`,
    tasks: "?tab=tasks",
    chat: (conversationId) => customerChatHref(slug, conversationId, customer.id),
  };
  const addTask = canUseCrm && active ? () => setComposing("TASK") : undefined;
  // Кнопки шапки — по праву; разложены по местам (имя важнее кнопок, QA-5 D3).
  const actions = {
    chatHref: canChat && active ? links.chat(overview.overview?.contact?.conversationId) : undefined,
    newOrderHref: canManageOrders && active ? `${base}/orders/new?customerId=${customer.id}` : undefined,
    onAddTask: addTask,
    onEdit: editable ? () => setEditing(true) : undefined,
    onAnonymize: editable ? card.anonymize : undefined,
    anonymizing: card.anonymizing,
  };
  // Пустую часть заголовку не передаём: иначе он рисует пустую строку под шапкой.
  const hasButtons: Record<CustomerHeaderPart, boolean> = {
    main: !!actions.newOrderHref,
    panel: !!(actions.chatHref || actions.onEdit),
    row: !!(actions.onAddTask || actions.onAnonymize),
  };
  const part = (p: CustomerHeaderPart) => (hasButtons[p] ? <CustomerHeaderActions {...actions} part={p} /> : undefined);

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title={customerTitle(customer)}
        subtitle={customerSubtitle(customer)}
        status={<Badge variant="outline">{CUSTOMER_TYPE_LABEL[customer.type]}</Badge>}
        back={back}
        action={part("main")}
        panelActions={part("panel")}
        secondaryActions={part("row")}
        secondaryBelow
      />

      <CustomerMetrics stats={stats} />

      <Tabs<CustomerTab>
        aria-label="Разделы карточки клиента"
        value={tab}
        onChange={setTab}
        items={[
          { value: "overview", label: "Обзор" },
          { value: "orders", label: "Заказы", count: stats?.ordersTotal },
          { value: "tasks", label: "Задачи", count: overview.overview?.tasksTotal },
          { value: "feed", label: "Лента" },
          { value: "profile", label: "Профиль" },
        ]}
      />

      {tab === "overview" ? (
        <CustomerOverviewTab
          state={overview}
          links={links}
          canUseCrm={canUseCrm && active}
          canChat={canChat && active}
          onAddTask={() => setComposing("TASK")}
          onAddNote={() => setComposing("NOTE")}
        />
      ) : null}
      {tab === "orders" ? <CustomerOrdersSection customerId={customer.id} /> : null}
      {tab === "tasks" ? (
        <CustomerTasksTab
          customerId={customer.id}
          canUseCrm={canUseCrm && active}
          onAdd={() => setComposing("TASK")}
          onChanged={overview.refresh}
          version={tasksVersion}
        />
      ) : null}
      {tab === "feed" ? <CustomerActivityFeedSection customerId={customer.id} /> : null}
      {tab === "profile" ? <CustomerProfileTab customer={customer} canManage={canManage} onChanged={card.refresh} /> : null}

      {composing ? (
        <ActivityComposerDialog
          kind={composing}
          target={{ customerId: customer.id }}
          onClose={() => setComposing(null)}
          onSaved={() => {
            setComposing(null);
            overview.refresh();
            setTasksVersion((n) => n + 1);
          }}
        />
      ) : null}

      {editing ? (
        <CustomerEditDialog
          open
          customer={customer}
          onClose={() => setEditing(false)}
          onSaved={(c) => {
            setEditing(false);
            card.setCustomer(c);
          }}
        />
      ) : null}

      {/* «Анонимизировать» — окно кабинета в опасном тоне, не системный confirm(). */}
      <ConfirmDialog request={card.confirm.request} onSettle={card.confirm.settle} />
    </div>
  );
}
